/**
 * « Présenter » : partager dans la salle ce que le centre montre. Ce que les deux plateformes partagent
 * (`usePresentationShare.ts` au natif, `usePresentationShare.web.ts` au navigateur) : la forme rendue,
 * les options de capture de l'ancien bouton (`common/liveMode/liveV3/ScreenShareToggleButton.web.js`),
 * et la lecture de « mon écran est-il partagé ».
 *
 * Aucun appel d'API, aucun message du canal de données : « Présenter » n'est qu'une piste LiveKit de
 * source `screen_share`, que les élèves reçoivent comme aujourd'hui.
 */
import { RoomEvent, Track, type Room } from "livekit-client"
import { useEffect, useState } from "react"

import { SCREEN_SHARE_CAPTURE_OPTIONS, type ScreenShareToggle } from "./screenShareToggle"

/** Le nom de la piste recadrée, celui de l'ancien bouton : un élève sur un ancien bundle la lit pareil. */
export const PRESENTATION_TRACK_NAME = "focus-screen-share"

/**
 * La capture recadrée : l'onglet courant proposé d'emblée (`preferCurrentTab`), une surface de
 * navigateur, que `cropTo` réduit ensuite à la région. L'ancien `getDisplayMedia`, à l'identique.
 */
export const PRESENTATION_CROP_CAPTURE = {
    video: { displaySurface: "browser" },
    preferCurrentTab: true,
} as const

/**
 * Sans `CropTarget` (Safari, Firefox) : l'onglet entier, avec son son, l'onglet courant proposé
 * d'emblée — l'ancien `setScreenShareEnabled(true, { audio, preferCurrentTab, selfBrowserSurface })`.
 */
export const PRESENTATION_FULL_TAB_OPTIONS = { ...SCREEN_SHARE_CAPTURE_OPTIONS, preferCurrentTab: true } as const

/** Ce que « Présenter » partage : la région recadrée, ou tout ce que l'écran montre (l'onglet, l'écran). */
export type PresentationCapture = "cropped" | "full"

/** Le partage de « Présenter », tel que l'écran du tuteur le lit. */
export type PresentationShare = {
    /** Mon écran est partagé dans la salle (une piste `screen_share` publiée), d'où que vienne le partage. */
    sharing: boolean
    /** Le partage en cours est celui de « Présenter », recadré sur la région (web, `CropTarget`). */
    cropped: boolean
    /** Le geste part : le sélecteur est ouvert, la piste se publie ou se retire. */
    pending: boolean
    /**
     * Ce que `start(region)` partagera, par la règle même qu'il suit : recadré quand le navigateur sait
     * recadrer (`CropTarget`) ET que la région est un élément à recadrer ; sinon tout (toujours au natif).
     * L'écran le lit avant `start` pour réduire la page au centre AVANT que la piste ne parte : une région
     * qui ne se recadre pas ne laisse jamais la page entière partir chez les élèves.
     */
    captureOf: (region: unknown) => PresentationCapture
    /**
     * Partager : recadré sur `region` (le nœud DOM du centre) quand le navigateur le sait, sinon
     * l'onglet entier (web) ou l'écran (natif). Rend vrai quand le partage est parti.
     */
    start: (region: unknown) => Promise<boolean>
    /** Arrêter le partage, recadré ou non. Rend vrai quand il est arrêté. */
    stop: () => Promise<boolean>
    /**
     * Le bouton de la barre au web : le même interrupteur que l'élève et que l'ancienne barre
     * (`useScreenShareToggle`), sans recadrage ; `null` quand il ne paraît pas. Toujours `null` au
     * natif, où la barre partage comme « Présenter » (l'ancien `LiveModeFooter.onScreenSharePress`).
     */
    webToggle: ScreenShareToggle | null
}

/** Mon écran est-il partagé : une publication de source `screen_share` existe (l'ancien `useScreenShare`). */
export function isScreenSharePublished(room: Room | undefined): boolean {
    return room?.localParticipant?.getTrackPublication(Track.Source.ScreenShare) != null
}

/** `isScreenSharePublished`, relu à chaque piste publiée ou retirée. */
export function useScreenSharePublished(room: Room | undefined): boolean {
    const [published, setPublished] = useState(() => isScreenSharePublished(room))
    useEffect(() => {
        const sync = () => setPublished(isScreenSharePublished(room))
        sync()
        if (room == null) return undefined
        room.on(RoomEvent.LocalTrackPublished, sync)
        room.on(RoomEvent.LocalTrackUnpublished, sync)
        return () => {
            room.off(RoomEvent.LocalTrackPublished, sync)
            room.off(RoomEvent.LocalTrackUnpublished, sync)
        }
    }, [room])
    return published
}

/** Le sélecteur du navigateur fermé sans choix : un geste du tuteur, pas une panne. */
export function isSharePickerDismissed(error: unknown): boolean {
    const name = (error as { name?: string } | null)?.name
    return name === "NotAllowedError" || name === "AbortError"
}
